import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { useToast } from '@/hooks/use-toast';
import { EMPLOYMENT_AGREEMENT_SYSTEM_PROMPT_JSON } from '@/lib/openai';
import { useUpdateTemplate } from '@/modules/templates/hooks/use-templates';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { TEMPLATE_CATEGORIES } from '@/modules/templates/constants/template-categories';
import { syncTemplateSave } from '../../lib/sync-template-uilm';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  Link as LinkIcon,
  Loader2,
  RefreshCw,
  RotateCcw,
  Wand2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { checkSlugAvailability } from '@/modules/templates/services/templates.service';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { buildIconOptions, getIconComponent } from '../../constants/icon-options';
import { OverwriteImportDialog } from '../overwrite-import-dialog';
import { useTemplateEditUiStore } from '../../store/use-template-edit-ui-store';

// Default prompts by template slug — pre-populates the AI prompt textarea.
const defaultPrompts: Record<string, string> = {
  'employment-agreement': EMPLOYMENT_AGREEMENT_SYSTEM_PROMPT_JSON,
};

const createFormSchema = (t: (key: string, params?: Record<string, unknown>) => string) =>
  z.object({
    slug: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_SLUGREQUIRED'))
      .max(100, t('ORGTEMPLATES_VALIDATION_SLUGMAX'))
      .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t('ORGTEMPLATES_VALIDATION_SLUGFORMAT')),
    title: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_TITLEREQUIRED'))
      .max(200, t('ORGTEMPLATES_VALIDATION_TITLEMAX')),
    description: z
      .string()
      .min(1, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONREQUIRED'))
      .max(1000, t('ORGTEMPLATES_VALIDATION_DESCRIPTIONMAX')),
    icon: z.string().default('FileText'),
    category: z.string().optional().nullable(),
    available: z.boolean().default(false),
    popular: z.boolean().default(false),
    keywords: z.array(z.string()).default([]).optional(),
    estimatedMinutes: z.number().int().positive().optional().nullable(),
    systemPromptRole: z.string().optional().nullable(),
    systemPrompt: z.string().optional().nullable(),
    uilmDescriptionKey: z.string().optional().nullable(),
    uilmTitleKey: z.string().optional().nullable(),
  });

type FormInput = z.input<ReturnType<typeof createFormSchema>>;
type FormData = z.output<ReturnType<typeof createFormSchema>>;

interface DetailsTabProps {
  template: TemplateWithScreens;
  templateData: TemplateWithScreens;
  refetchTemplate: () => void;
  /** Lifted icon value so the page header can render the live icon as the
   *  user picks one in the form. */
  onIconChange?: (icon: string) => void;
}

export const DetailsTab = ({
  template,
  templateData,
  refetchTemplate,
  onIconChange,
}: DetailsTabProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateTemplateMutation = useUpdateTemplate();

  const formSchema = createFormSchema(t);
  const iconOptions = buildIconOptions(t);

  const [keywordInput, setKeywordInput] = useState('');
  const [aiConfigExpanded, setAiConfigExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const slugCheckTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Overwrite dialog open-state lives in Zustand so the page header's
  // "Overwrite from JSON" button can trigger it without prop drilling.
  const overwriteDialogOpen = useTemplateEditUiStore((s) => s.overwriteDialogOpen);
  const setOverwriteDialogOpen = useTemplateEditUiStore((s) => s.setOverwriteDialogOpen);

  // Preview token state (still placeholder — TODO: migrate to UDS service)
  const [previewToken, setPreviewToken] = useState<string | null>(
    templateData.previewToken || null
  );
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isRevokingToken, setIsRevokingToken] = useState(false);

  const form = useForm<FormInput, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slug: '',
      title: '',
      description: '',
      icon: 'FileText',
      category: null,
      available: false,
      popular: false,
      keywords: [],
      estimatedMinutes: null,
      systemPromptRole: null,
      systemPrompt: null,
      uilmTitleKey: null,
      uilmDescriptionKey: null,
    },
  });

  // Realtime slug availability (mirrors the create flow). Excludes this
  // template's own id so its current slug isn't reported as "taken".
  const checkSlug = useCallback(
    async (slug: string) => {
      if (!slug || slug.length < 2 || slug === templateData.slug) {
        setSlugAvailable(null);
        return;
      }
      setIsCheckingSlug(true);
      try {
        setSlugAvailable(await checkSlugAvailability(slug, template.id));
      } catch {
        setSlugAvailable(null);
      } finally {
        setIsCheckingSlug(false);
      }
    },
    [template.id, templateData.slug]
  );

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    form.setValue('slug', slug);
    if (slugCheckTimeout.current) clearTimeout(slugCheckTimeout.current);
    slugCheckTimeout.current = setTimeout(() => checkSlug(slug), 300);
  };

  // Populate form when templateData changes (initial load + after refetch).
  useEffect(() => {
    form.reset({
      slug: templateData.slug,
      title: templateData.title,
      description: templateData.description ?? '',
      icon: templateData.icon ?? 'FileText',
      category: templateData.category || null,
      available: templateData.available ?? false,
      popular: templateData.popular ?? false,
      keywords: Array.isArray((templateData as unknown as { keywords?: string[] }).keywords)
        ? ((templateData as unknown as { keywords?: string[] }).keywords ?? [])
        : [],
      estimatedMinutes: templateData.estimatedMinutes ?? null,
      systemPromptRole: templateData.systemPromptRole || null,
      systemPrompt: templateData.systemPrompt || null,
      uilmTitleKey: templateData.uilmTitleKey || null,
      uilmDescriptionKey: templateData.uilmDescriptionKey || null,
    });
    setPreviewToken(templateData.previewToken || null);
    setAiConfigExpanded(!!templateData.systemPrompt || !!templateData.systemPromptRole);
  }, [templateData, form]);

  // Hoist the live icon value to the page header.
  const watchedIcon = form.watch('icon');
  useEffect(() => {
    onIconChange?.(watchedIcon ?? 'FileText');
  }, [watchedIcon, onIconChange]);

  const handleSubmit = async (data: FormInput) => {
    if (!templateData?.id) return;
    setIsSubmitting(true);
    try {
      await updateTemplateMutation.mutateAsync({
        itemId: templateData.id,
        input: {
          Slug: data.slug,
          Title: data.title,
          Description: data.description,
          Icon: data.icon,
          Category: data.category ?? undefined,
          Available: data.available,
          Popular: data.popular,
          Keywords: data.keywords,
          EstimatedMinutes: data.estimatedMinutes ?? undefined,
          SystemPromptRole: data.systemPromptRole ?? undefined,
          SystemPrompt: data.systemPrompt ?? undefined,
          UilmTitleKey: data.uilmTitleKey ?? undefined,
          UilmDescriptionKey: data.uilmDescriptionKey ?? undefined,
        },
      });

      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_TOAST_UPDATESUCCESS') });

      // Push the new title + description to UILM. Only for system templates
      // (admin route); org-owned templates skip the sync. Soft-fail: a UILM
      // error is surfaced as a deferred toast — the UDS update already
      // succeeded and admin can retry from the Translations tab.
      const isSystemTemplate = !templateData.organizationId;
      if (isSystemTemplate) {
        try {
          await syncTemplateSave({
            templateId: templateData.id,
            slug: data.slug,
            title: data.title,
            description: data.description,
          });
        } catch (uilmErr) {
          console.warn('UILM sync deferred for template update:', uilmErr);
          toast({
            variant: 'default',
            title: 'Translation sync deferred',
            description:
              'Template was updated, but translation keys could not be pushed to UILM. Open the Translations tab to retry.',
          });
        }
      }

      refetchTemplate();
    } catch (error) {
      console.error('Error updating template:', error);
      toast({
        variant: 'destructive',
        title: error instanceof Error ? error.message : t('ORGTEMPLATES_EDIT_TOAST_UPDATEERROR'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentFieldCount = template.screens?.reduce(
    (count, screen) => count + (screen.fields?.length || 0),
    0
  );

  const addKeyword = () => {
    const keyword = keywordInput.trim().toLowerCase();
    const currentKeywords = form.getValues('keywords') ?? [];
    if (keyword && !currentKeywords.includes(keyword)) {
      form.setValue('keywords', [...currentKeywords, keyword]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keyword: string) => {
    const currentKeywords = form.getValues('keywords') ?? [];
    form.setValue(
      'keywords',
      currentKeywords.filter((k) => k !== keyword)
    );
  };

  // Preview token handlers (placeholder — not yet migrated to UDS)
  const handleGeneratePreviewToken = async () => {
    setIsGeneratingToken(true);
    try {
      // TODO: Migrate preview token generation to UDS service
      toast({
        variant: 'destructive',
        title: 'Preview token generation not yet migrated to UDS',
      });
    } catch (error) {
      console.error('Error generating preview token:', error);
      toast({
        variant: 'destructive',
        title:
          error instanceof Error
            ? error.message
            : t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_GENERATEERROR'),
      });
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleRevokePreviewToken = async () => {
    setIsRevokingToken(true);
    try {
      // TODO: Migrate preview token revocation to UDS service
      setPreviewToken(null);
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_REVOKED') });
    } catch (error) {
      console.error('Error revoking preview token:', error);
      toast({
        variant: 'destructive',
        title:
          error instanceof Error ? error.message : t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_REVOKEERROR'),
      });
    } finally {
      setIsRevokingToken(false);
    }
  };

  const handleCopyPreviewLink = async () => {
    if (!previewToken || !template?.slug) return;

    const previewUrl = `${window.location.origin}/templates/${template.slug}/generate?preview=${previewToken}`;

    try {
      await navigator.clipboard.writeText(previewUrl);
      toast({ variant: 'success', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_COPIED') });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({ variant: 'destructive', title: t('ORGTEMPLATES_EDIT_PREVIEW_TOAST_COPYERROR') });
    }
  };

  const IconComponent = getIconComponent(form.watch('icon'));

  return (
    <>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('ORGTEMPLATES_FORM_TITLE')}</CardTitle>
            <CardDescription>{t('ORGTEMPLATES_FORM_DESCRIPTION')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Title and Slug */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">{t('ORGTEMPLATES_FORM_TITLELABEL')}</Label>
                <Input
                  id="title"
                  placeholder={t('ORGTEMPLATES_FORM_TITLEPLACEHOLDER')}
                  {...form.register('title')}
                />
                {form.formState.errors.title && (
                  <p className="text-sm text-destructive">{form.formState.errors.title.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">{t('ORGTEMPLATES_FORM_SLUGLABEL')}</Label>
                <div className="relative">
                  <Input
                    id="slug"
                    placeholder={t('ORGTEMPLATES_FORM_SLUGPLACEHOLDER')}
                    value={form.watch('slug')}
                    onChange={handleSlugChange}
                    className={
                      slugAvailable === false
                        ? 'border-destructive pr-10'
                        : slugAvailable === true
                          ? 'border-lime-green pr-10'
                          : 'pr-10'
                    }
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {isCheckingSlug ? (
                      <Loader2 className="size-4 animate-spin text-globe-grey" />
                    ) : slugAvailable === true ? (
                      <Check className="size-4 text-lime-green" />
                    ) : slugAvailable === false ? (
                      <AlertCircle className="size-4 text-destructive" />
                    ) : null}
                  </div>
                </div>
                <p className="text-sm text-globe-grey">
                  {t('ADMIN_TEMPLATES_EDIT_FORM_URLPREVIEW', {
                    url: `/templates/${form.watch('slug') || 'slug'}/generate`,
                  })}
                </p>
                {form.formState.errors.slug && (
                  <p className="text-sm text-destructive">{form.formState.errors.slug.message}</p>
                )}
                {slugAvailable === false && (
                  <p className="text-sm text-destructive">{t('ORGTEMPLATES_FORM_SLUGTAKEN')}</p>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">{t('ORGTEMPLATES_FORM_DESCRIPTIONLABEL')}</Label>
              <textarea
                id="description"
                placeholder={t('ORGTEMPLATES_FORM_DESCRIPTIONPLACEHOLDER')}
                className="flex min-h-24 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                {...form.register('description')}
              />
              {form.formState.errors.description && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>{t('ORGTEMPLATES_FORM_ICONLABEL')}</Label>
              <div className="flex items-center gap-2">
                <div className="flex size-10 items-center justify-center rounded-lg border border-border bg-muted">
                  <IconComponent className="size-5 text-selise-blue" />
                </div>
                <select
                  className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.watch('icon')}
                  onChange={(e) => form.setValue('icon', e.target.value)}
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label>Category</Label>
              <select
                className="flex h-10 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={form.watch('category') ?? ''}
                onChange={(e) => form.setValue('category', e.target.value || null)}
              >
                <option value="">Select a category</option>
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  checked={form.watch('available')}
                  onChange={(e) => form.setValue('available', e.target.checked)}
                />
                <span className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('ORGTEMPLATES_FORM_AVAILABLELABEL')}
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  checked={form.watch('popular')}
                  onChange={(e) => form.setValue('popular', e.target.checked)}
                />
                <span className="text-sm font-medium text-[hsl(var(--fg))]">
                  {t('ORGTEMPLATES_FORM_POPULARLABEL')}
                </span>
              </label>
            </div>

            {/* Keywords */}
            <div className="space-y-2">
              <Label>{t('ORGTEMPLATES_FORM_KEYWORDSLABEL')}</Label>
              <div className="flex gap-2">
                <Input
                  placeholder={t('ORGTEMPLATES_FORM_KEYWORDSPLACEHOLDER')}
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addKeyword();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addKeyword}>
                  {t('ORGTEMPLATES_FORM_KEYWORDSADD')}
                </Button>
              </div>
              {(form.watch('keywords') ?? []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(form.watch('keywords') || []).map((keyword) => (
                    <Badge key={keyword} variant="secondary" className="gap-1 pr-1">
                      {keyword}
                      <button
                        type="button"
                        onClick={() => removeKeyword(keyword)}
                        className="ml-1 rounded-full hover:bg-destructive/20"
                      >
                        <X className="size-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Estimated Minutes */}
            <div className="space-y-2">
              <Label htmlFor="estimatedMinutes">
                {t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESLABEL')}
              </Label>
              <Input
                id="estimatedMinutes"
                type="number"
                min="1"
                placeholder={t('ORGTEMPLATES_FORM_ESTIMATEDMINUTESPLACEHOLDER')}
                {...form.register('estimatedMinutes', {
                  setValueAs: (v) => (v === '' ? null : parseInt(v, 10)),
                })}
              />
            </div>
          </CardContent>
        </Card>

        {/* Internal Preview Link Card - Only show for draft templates */}
        {!form.watch('available') && (
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-selise-blue" />
                <CardTitle>{t('ORGTEMPLATES_EDIT_PREVIEW_TITLE')}</CardTitle>
              </div>
              <CardDescription>{t('ORGTEMPLATES_EDIT_PREVIEW_DESCRIPTION')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {previewToken ? (
                <>
                  <div className="space-y-2">
                    <Label>{t('ORGTEMPLATES_EDIT_PREVIEW_URLLABEL')}</Label>
                    <div className="flex gap-2">
                      <Input
                        readOnly
                        value={`${window.location.origin}/templates/${form.watch('slug') || template?.slug || 'slug'}/generate?preview=${previewToken}`}
                        className="font-mono text-sm"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleCopyPreviewLink}
                        title={t('ORGTEMPLATES_EDIT_PREVIEW_COPYTITLE')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-globe-grey">
                      {t('ORGTEMPLATES_EDIT_PREVIEW_URLHELP')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleGeneratePreviewToken}
                      disabled={isGeneratingToken}
                      className="gap-2"
                    >
                      {isGeneratingToken ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('ORGTEMPLATES_EDIT_PREVIEW_GENERATING')}
                        </>
                      ) : (
                        <>
                          <RefreshCw className="h-4 w-4" />
                          {t('ORGTEMPLATES_EDIT_PREVIEW_REGENERATE')}
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleRevokePreviewToken}
                      disabled={isRevokingToken}
                      className="gap-2 border-destructive text-destructive hover:text-destructive"
                    >
                      {isRevokingToken ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('ORGTEMPLATES_EDIT_PREVIEW_REVOKING')}
                        </>
                      ) : (
                        <>
                          <X className="h-4 w-4" />
                          {t('ORGTEMPLATES_EDIT_PREVIEW_REVOKE')}
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-selise-blue/5 border border-selise-blue/20 rounded-lg">
                    <div className="flex items-start gap-3">
                      <LinkIcon className="h-5 w-5 text-selise-blue mt-0.5" />
                      <div>
                        <p className="font-medium text-[hsl(var(--fg))]">
                          {t('ORGTEMPLATES_EDIT_PREVIEW_EMPTYTITLE')}
                        </p>
                        <p className="text-sm text-globe-grey mt-1">
                          {t('ORGTEMPLATES_EDIT_PREVIEW_EMPTYDESCRIPTION')}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    type="button"
                    onClick={handleGeneratePreviewToken}
                    disabled={isGeneratingToken}
                    className="gap-2"
                  >
                    {isGeneratingToken ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {t('ORGTEMPLATES_EDIT_PREVIEW_GENERATING')}
                      </>
                    ) : (
                      <>
                        <LinkIcon className="h-4 w-4" />
                        {t('ORGTEMPLATES_EDIT_PREVIEW_GENERATE')}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* AI Configuration Card */}
        <Card className="mb-6">
          <CardHeader
            className="cursor-pointer"
            onClick={() => setAiConfigExpanded(!aiConfigExpanded)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-selise-blue" />
                <CardTitle>{t('ORGTEMPLATES_EDIT_AI_TITLE')}</CardTitle>
                {(form.watch('systemPrompt') || form.watch('systemPromptRole')) && (
                  <Badge variant="secondary" className="text-xs">
                    {t('ORGTEMPLATES_EDIT_AI_CUSTOMBADGE')}
                  </Badge>
                )}
              </div>
              {aiConfigExpanded ? (
                <ChevronUp className="h-5 w-5 text-globe-grey" />
              ) : (
                <ChevronDown className="h-5 w-5 text-globe-grey" />
              )}
            </div>
            <CardDescription>{t('ORGTEMPLATES_EDIT_AI_DESCRIPTION')}</CardDescription>
          </CardHeader>
          {aiConfigExpanded && (
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="systemPromptRole">{t('ORGTEMPLATES_EDIT_AI_ROLELABEL')}</Label>
                <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_EDIT_AI_ROLEHELP')}</p>
                <Input
                  id="systemPromptRole"
                  placeholder={t('ORGTEMPLATES_EDIT_AI_ROLEPLACEHOLDER')}
                  {...form.register('systemPromptRole')}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="systemPrompt">{t('ORGTEMPLATES_EDIT_AI_PROMPTLABEL')}</Label>
                  {defaultPrompts[form.watch('slug')] && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const defaultPrompt = defaultPrompts[form.watch('slug')];
                        if (defaultPrompt) {
                          form.setValue('systemPrompt', defaultPrompt);
                        }
                      }}
                      className="h-7 text-sm gap-1"
                    >
                      <RotateCcw className="size-3" />
                      {t('ORGTEMPLATES_EDIT_AI_LOADDEFAULT')}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-globe-grey">{t('ORGTEMPLATES_EDIT_AI_PROMPTHELP')}</p>
                <textarea
                  id="systemPrompt"
                  placeholder={t('ORGTEMPLATES_EDIT_AI_PROMPTPLACEHOLDER')}
                  className="flex min-h-64 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  {...form.register('systemPrompt')}
                />
                {form.watch('systemPrompt') && (
                  <div className="flex justify-between items-center text-sm text-globe-grey">
                    <span>
                      {t('ORGTEMPLATES_EDIT_AI_CHARACTERS', {
                        count: form.watch('systemPrompt')?.length ?? 0,
                      })}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => form.setValue('systemPrompt', null)}
                      className="h-6 text-sm text-destructive hover:text-destructive"
                    >
                      {t('ORGTEMPLATES_EDIT_AI_CLEAR')}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Actions — Export/Overwrite now live in the page header.
            Only Cancel/Submit remain here. */}
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" size={'sm'} asChild>
            <Link to={`/admin/templates`}>Cancel</Link>
          </Button>
          <Button type="submit" disabled={isSubmitting} size={'sm'}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {t('ORGTEMPLATES_EDIT_ACTIONS_SUBMIT')}
          </Button>
        </div>
      </form>

      <OverwriteImportDialog
        open={overwriteDialogOpen}
        onOpenChange={setOverwriteDialogOpen}
        templateId={template.id}
        templateSlug={template.slug}
        currentScreenCount={template.screens.length}
        currentFieldCount={currentFieldCount}
        onSuccess={async () => {
          // invalidate and refetch template to get the updated data after import
          refetchTemplate();
        }}
      />
    </>
  );
};

export default DetailsTab;
